import { useState, useEffect } from 'react';
import { Save, Settings2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface MetafieldConfig {
  namespace: string;
  key: string;
}

export interface ShopSettings {
  siteMetafield?: MetafieldConfig | null;
  distributorIdMetafield?: MetafieldConfig | null;
}

interface MetafieldConfigCardProps {
  settings: ShopSettings | null;
  onSave: (settings: ShopSettings) => Promise<void>;
  isLoading: boolean;
}

export function MetafieldConfigCard({ settings, onSave, isLoading }: MetafieldConfigCardProps) {
  const [siteNamespace, setSiteNamespace] = useState('');
  const [siteKey, setSiteKey] = useState('');
  const [distNamespace, setDistNamespace] = useState('');
  const [distKey, setDistKey] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings) {
      setSiteNamespace(settings.siteMetafield?.namespace || '');
      setSiteKey(settings.siteMetafield?.key || '');
      setDistNamespace(settings.distributorIdMetafield?.namespace || '');
      setDistKey(settings.distributorIdMetafield?.key || '');
    }
  }, [settings]);

  useEffect(() => {
    const original = settings || {};
    const changed =
      siteNamespace !== (original.siteMetafield?.namespace || '') ||
      siteKey !== (original.siteMetafield?.key || '') ||
      distNamespace !== (original.distributorIdMetafield?.namespace || '') ||
      distKey !== (original.distributorIdMetafield?.key || '');
    setHasChanges(changed);
  }, [siteNamespace, siteKey, distNamespace, distKey, settings]);

  const handleSave = async () => {
    const newSettings: ShopSettings = {};

    if (siteNamespace && siteKey) {
      newSettings.siteMetafield = { namespace: siteNamespace, key: siteKey };
    }

    if (distNamespace && distKey) {
      newSettings.distributorIdMetafield = { namespace: distNamespace, key: distKey };
    }

    await onSave(newSettings);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          Metafield Configuration
        </CardTitle>
        <CardDescription>
          Configure which Shopify customer metafields identify distributors. These metafields are used
          to look up distributors during attribution.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h4 className="font-medium text-sm">Site Metafield</h4>
          <p className="text-sm text-muted-foreground">
            The metafield that stores the distributor's site name (e.g., "mysite", "john123")
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="site-namespace">Namespace</Label>
              <Input
                id="site-namespace"
                placeholder="custom"
                value={siteNamespace}
                onChange={(e) => setSiteNamespace(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-key">Key</Label>
              <Input
                id="site-key"
                placeholder="site"
                value={siteKey}
                onChange={(e) => setSiteKey(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="border-t pt-6 space-y-4">
          <h4 className="font-medium text-sm">Distributor ID Metafield</h4>
          <p className="text-sm text-muted-foreground">
            The metafield that stores the distributor's unique ID (e.g., "DIST_001")
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dist-namespace">Namespace</Label>
              <Input
                id="dist-namespace"
                placeholder="custom"
                value={distNamespace}
                onChange={(e) => setDistNamespace(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dist-key">Key</Label>
              <Input
                id="dist-key"
                placeholder="distributor_id"
                value={distKey}
                onChange={(e) => setDistKey(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={isLoading || !hasChanges}>
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
